import { Badge } from "@/components/ui/badge";
import { Tooltip } from "@/components/ui/tooltip";
import type { SessionListItem } from "@/features/sessions";
import { classificationColor } from "@/features/sessions/classification";
import { effectiveResult, resultTone } from "@/features/sessions/result";
import { useTranslation } from "@/features/utils/i18n";
import {
  BellRingingIcon,
  ChatCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  EnvelopeIcon,
  type Icon,
  PhoneIncomingIcon,
  PhoneOutgoingIcon,
  PhoneSlashIcon,
  PhoneTransferIcon,
  QuestionIcon,
  VoicemailIcon,
  XCircleIcon,
} from "@phosphor-icons/react";

/**
 * The read-only vocabulary of a conversation: how its channel, its outcome and
 * its classification are drawn. Shared by the list — where they are icons, the
 * only way a mail-style row can carry eight columns' worth of metadata — and by
 * the conversation header, where the same things are labelled badges. One
 * source, so a green check never means two different things between the panes —
 * and the calls table (dashboard, contact file) draws its cells with the same
 * badges, reduced to their glyph.
 *
 * Three concepts, three shapes: the channel is an outlined badge with its glyph,
 * the outcome a tinted badge in its semantic tone, a classification a neutral
 * badge carrying the dot of its colour on the dashboard's chart.
 */

/** A duration in seconds, written the way a call log writes it. */
export const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
};

/**
 * The channel a conversation happened on. A call also carries its direction —
 * an outbound call is a different thing from an inbound one to whoever reads
 * the list — while a chat or an e-mail is just itself.
 *
 * Only `call` is read as a call, and everything that is not an e-mail falls
 * back to the chat bubble: the backend stores `medium: "web"` for a session
 * opened without a phone number (`session.ts`, `medium: … : "web"`), which the
 * `"call" | "chat" | "email"` type does not list — and a web chat drawn with a
 * green inbound-call glyph is a lie about where the conversation happened.
 */
export function useChannel(session: Pick<SessionListItem, "type" | "medium">) {
  const { t } = useTranslation("calls");

  // `tone` is the `Badge` colour, `color` the same tone for a bare glyph.
  if (session.medium === "email")
    return {
      Icon: EnvelopeIcon,
      label: t("mediums.email"),
      tone: "purple",
      color: "var(--purple)",
    };

  if (session.medium === "call") {
    if (session.type === "outbound")
      return {
        Icon: PhoneOutgoingIcon,
        label: t("types.outbound"),
        tone: "blue",
        color: "var(--info)",
      };

    if (session.type === "notifications")
      return {
        Icon: BellRingingIcon,
        label: t("types.notifications"),
        tone: "orange",
        color: "var(--warning)",
      };

    return {
      Icon: PhoneIncomingIcon,
      label: t("types.inbound"),
      tone: "green",
      color: "var(--success)",
    };
  }

  return {
    Icon: ChatCircleIcon,
    label: t("mediums.chat"),
    tone: "blue",
    color: "var(--info)",
  };
}

/** The channel as a badge: the conversation header. */
export function ChannelBadge({
  session,
  className,
}: {
  session: Pick<SessionListItem, "type" | "medium">;
  className?: string;
}) {
  const { Icon, label, tone } = useChannel(session);
  return (
    <Badge variant="outline" color={tone} className={className}>
      <Icon className="h-3 w-3 shrink-0" weight="fill" />
      {label}
    </Badge>
  );
}

/** The channel as its glyph alone, named by its tooltip: the calls table's narrow column. */
export function ChannelIcon({
  session,
}: {
  session: Pick<SessionListItem, "type" | "medium">;
}) {
  const { Icon, label, tone } = useChannel(session);
  return (
    <Tooltip content={label}>
      <Badge
        variant="outline"
        color={tone}
        className="px-1.5"
        role="img"
        aria-label={label}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" weight="fill" />
      </Badge>
    </Tooltip>
  );
}

const RESULT_ICONS: Record<string, Icon> = {
  success: CheckCircleIcon,
  failure: XCircleIcon,
  no_answer: PhoneSlashIcon,
  voicemail: VoicemailIcon,
  transferred: PhoneTransferIcon,
  pending: ClockIcon,
  unknown: QuestionIcon,
};

/** The session's outcome (`effectiveResult`) with its glyph, label and colour. */
export function useResult(
  session: Pick<
    SessionListItem,
    "state" | "result" | "transferred" | "tool_failed"
  >,
) {
  const { t } = useTranslation("calls");
  const result = effectiveResult(session);

  return {
    result,
    Icon: RESULT_ICONS[result] || QuestionIcon,
    label: t(`results.${result}`, result),
    color: resultTone(result),
  };
}

/**
 * The outcome as a coloured pill — the one piece of metadata a row is scanned
 * for, so it is the one the list does not reduce to a glyph.
 */
export function ResultBadge({
  session,
  className,
}: {
  session: Pick<
    SessionListItem,
    "state" | "result" | "transferred" | "tool_failed"
  >;
  className?: string;
}) {
  const { Icon, label, color } = useResult(session);
  return (
    <Badge variant="outline" color={color} className={className}>
      <Icon className="h-3 w-3 shrink-0" weight="fill" />
      {label}
    </Badge>
  );
}

/** The outcome as its tinted glyph alone, named by its tooltip. */
export function ResultIcon({
  session,
}: {
  session: Pick<SessionListItem, "state" | "result" | "transferred">;
}) {
  const { Icon, label, color } = useResult(session);
  return (
    <Tooltip content={label}>
      <Badge
        variant="outline"
        color={color}
        className="px-1.5"
        role="img"
        aria-label={label}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" weight="fill" />
      </Badge>
    </Tooltip>
  );
}

/** The tenant-facing label of a classification tag. */
export function useTagLabel() {
  const { t } = useTranslation("calls");

  const labels: Record<string, string> = {
    booking_creation: t("classifications.booking_creation"),
    booking_reschedule: t("classifications.booking_reschedule"),
    booking_cancellation: t("classifications.booking_cancellation"),
    transfer: t("classifications.transfer"),
    qualification_form: t("classifications.qualification_form"),
  };

  return (tag: string) => labels[tag] || tag.replace(/_/g, " ");
}

/** A classification tag: a neutral badge with the dot the dashboard's legend uses. */
export function TagBadge({
  tag,
  className,
}: {
  tag: string;
  className?: string;
}) {
  const label = useTagLabel()(tag);
  return (
    <Badge variant="secondary" className={className}>
      <span
        aria-hidden
        className="size-2 shrink-0 rounded-full"
        style={{ backgroundColor: classificationColor(tag) }}
      />
      {label}
    </Badge>
  );
}

/**
 * One piece of metadata, as an icon and (optionally) its value. The tooltip
 * carries the column header the table used to print, which is what lets the row
 * itself stay quiet enough to read at a glance.
 */
export function MetaIcon({
  icon: IconComponent,
  label,
  value,
  color,
  className,
}: {
  icon: Icon;
  label: string;
  value?: string;
  color?: string;
  className?: string;
}) {
  return (
    <Tooltip content={value ? `${label} : ${value}` : label}>
      <span
        className={`inline-flex min-w-0 items-center gap-1 text-xs text-muted-foreground ${className || ""}`}
      >
        <IconComponent
          className="h-3.5 w-3.5 shrink-0"
          weight="fill"
          style={color ? { color } : undefined}
        />
        {value && <span className="truncate">{value}</span>}
      </span>
    </Tooltip>
  );
}
